import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { navigationLinks } from "@/mock/siteContent";

const Logo = () => (
  <Link to="/" data-testid="logo-link" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
    
    <span style={{
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      fontSize: "1.05rem",
      letterSpacing: "0.14em",
      color: "#fafafa",
      textTransform: "uppercase",
    }}>
      BIM<span style={{ color: "var(--gold)" }}>tec</span>
    </span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItemStyle = (isActive) => ({
    fontFamily: "'DM Mono', monospace",
    fontSize: "0.7rem",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: isActive ? "var(--gold)" : "var(--muted)",
    textDecoration: "none",
    padding: "6px 0",
    borderBottom: isActive ? "1px solid var(--gold)" : "1px solid transparent",
    transition: "color 0.2s, border-color 0.2s",
  });

  return (
    <header data-testid="main-header" style={{
      position: "fixed",
      inset: "0 0 auto 0",
      zIndex: 50,
      borderBottom: scrolled ? "1px solid var(--border)" : "1px solid transparent",
      background: scrolled ? "rgba(9,9,11,0.95)" : "rgba(9,9,11,0.6)",
      backdropFilter: "blur(20px)",
      transition: "border-color 0.3s, background 0.3s",
    }}>
      <div style={{
        maxWidth: 1140,
        margin: "0 auto",
        padding: "0 24px",
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <Logo />
        <nav style={{ display: "flex", alignItems: "center", gap: 32 }} className="desktop-nav">
          {navigationLinks.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              data-testid={`nav-${item.path.replace("/", "") || "home"}`}
              style={({ isActive }) => navItemStyle(isActive)}
            >
              {item.label}
            </NavLink>
          ))}
          <Link to="/contato" className="btn-gold" data-testid="nav-cta-button" style={{ padding: "8px 18px", fontSize: "0.68rem" }}>
            Fale conosco
          </Link>
        </nav>
        <button
          onClick={() => setOpen((p) => !p)}
          data-testid="mobile-menu-toggle"
          style={{
            display: "none",
            background: "none",
            border: "1px solid var(--border)",
            borderRadius: 4,
            padding: "7px 10px",
            cursor: "pointer",
            color: "var(--text)",
          }}
          className="mobile-toggle"
          aria-label="Menu"
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 4, width: 18 }}>
            {[0,1,2].map(i => (
              <span key={i} style={{
                display: "block", height: 1.5, background: "var(--muted)", borderRadius: 1,
                transition: "0.2s",
                transform: open && i === 0 ? "translateY(5.5px) rotate(45deg)"
                          : open && i === 2 ? "translateY(-5.5px) rotate(-45deg)" : "none",
                opacity: open && i === 1 ? 0 : 1,
              }} />
            ))}
          </span>
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" style={{ background: "rgba(9,9,11,0.98)", borderTop: "1px solid var(--border)", padding: "16px 24px 20px" }}>
          {navigationLinks.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              onClick={() => setOpen(false)}
              style={({ isActive }) => ({
                ...navItemStyle(isActive),
                display: "block",
                padding: "12px 0",
                borderBottom: "1px solid var(--border)",
              })}
            >
              {item.label}
            </NavLink>
          ))}
          <Link to="/contato" className="btn-gold" onClick={() => setOpen(false)}
            style={{ marginTop: 16, width: "100%", justifyContent: "center" }}>
            Fale conosco
          </Link>
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: flex !important; }
        }
      `}</style>
    </header>
  );
};

const Footer = () => (
  <footer data-testid="main-footer" style={{ borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
    <div className="footer-flex" style={{
      maxWidth: 1140, margin: "0 auto", padding: "28px 24px",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
    }}>
      <Logo />
      <p style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.1em", color: "var(--muted2)" }}>
        © 2026 BIMtec Tecnologia da Construção — Engenharia BIM · Canaã dos Carajás, PA
      </p>
    </div>
  </footer>
);

const MainLayout = () => (
  <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative", zIndex: 1 }}>
    <Header />
    <Toaster richColors closeButton position="top-right" />
    <main style={{ flex: 1, paddingTop: 64 }}>
      <Outlet />
    </main>
    <Footer />
    <a
      href="https://wa.me/5594991146693?text=Ol%C3%A1%2C%20tenho%20interesse%20em%20projetos%20BIM%20e%20gostaria%20de%20falar%20com%20um%20especialista%20para%20tirar%20algumas%20d%C3%BAvidas"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp BIMtec"
      data-testid="whatsapp-button"
      style={{ position: "fixed", bottom: 24, right: 24, zIndex: 60, display: "block", transition: "transform 0.2s" }}
      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
    >
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
        alt="WhatsApp"
        style={{ height: 52, width: 52, objectFit: "contain" }}
      />
    </a>
  </div>
);

export default MainLayout;
